import { clearPermCache } from '../hooks/usePermissions';
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import toast from '../utils/toast';
import { getToken, removeToken } from '../utils/authStorage';

const WebSocketContext = createContext(null);

export function WebSocketProvider({ children }) {
  const { user, updateBalance } = useAuth();
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const reconnectRef  = useRef(null);
  const retryCountRef = useRef(0);
  const MAX_RETRIES   = 15; // increase retries — support chat needs persistent connection
  const listenersRef = useRef({});
  // Always-fresh user ref — avoids stale closure in handleMessage
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  const subscribe = useCallback((event, callback) => {
    if (!listenersRef.current[event]) listenersRef.current[event] = [];
    listenersRef.current[event].push(callback);
    return () => {
      listenersRef.current[event] = listenersRef.current[event].filter(cb => cb !== callback);
    };
  }, []);

  const emit = useCallback((event, data) => {
    const handlers = listenersRef.current[event] || [];
    handlers.forEach(h => h(data));
  }, []);

  // ✅ FIX: handleMessage moved inside useCallback to fix react-hooks/exhaustive-deps
  const handleMessage = useCallback((msg) => {
    switch (msg.type) {
      case 'connected':
        setConnected(true);
        emit('connected', msg);
        break;

      case 'balance_update':
        updateBalance(msg.data);
        emit('balance_update', msg.data);
        break;

      case 'notification': {
        setNotifications(prev => [msg.data, ...prev].slice(0, 50));
        emit('notification', msg.data);

        // Suppress support reply toast on support page
        const isSupportReply = msg.data?.action_url?.includes('/support') &&
          msg.data?.title?.includes('Support Reply');
        const onSupportPage  = window.location.pathname.startsWith('/support');
        if (isSupportReply && onSupportPage) break;

        // Suppress spin win toast everywhere — result modal already shows reward
        const isSpinWin = msg.data?.category === 'spin_win';
        if (isSpinWin) break;

        if (msg.data.type === 'success') {
          toast.success(msg.data.message);
        } else if (msg.data.type === 'error') {
          toast.error(msg.data.message);
        } else {
          toast(msg.data.message, { icon: msg.data.type === 'warning' ? '⚠️' : 'ℹ️' });
        }
        break;
      }

      case 'recharge_success':
        emit('recharge_success', msg.data);
        toast.success(`Recharge of Rs.${msg.data.amount} successful!`);
        break;

      case 'withdrawal_status_update':
        emit('withdrawal_status_update', msg.data);
        if (msg.data.status === 'approved') toast.success('Withdrawal approved!');
        if (msg.data.status === 'rejected') {
          const reason = msg.data.reason;
          toast.error(reason && reason !== 'Rejected by admin' ? `Withdrawal rejected: ${reason}` : 'Withdrawal rejected by admin');
        }
        break;

      case 'vip_upgrade':
        emit('vip_upgrade', msg.data);
        window.dispatchEvent(new CustomEvent('auth:vip-upgrade', { detail: msg.data || {} }));
        toast.success(`VIP level upgraded. You are now VIP ${msg.data.new_level}.`, { duration: 6000 });
        break;

      case 'investment_created':
        emit('investment_created', msg.data);
        break;

      case 'claim_success':
        emit('claim_success', msg.data);
        break;

      case 'commission_received':
        emit('commission_received', msg.data);
        toast.success(`Referral commission received: Rs.${msg.data.amount}`);
        break;

      case 'force_logout': {
        // Admin banned or suspended this user — log out immediately
        // PROTECTION: super_admin is NEVER force-logged out
        if (userRef.current?.role === 'super_admin') break; // super admin immune

        removeToken();
        sessionStorage.clear();
        const flReason = msg.data?.reason || 'suspended';
        const flMsg = flReason === 'banned'
          ? 'Your account has been banned. Contact support.'
          : 'Your account has been suspended. Contact support.';
        toast.error(flMsg, { duration: 8000 });
        window.dispatchEvent(new CustomEvent('auth:force-logout', { detail: { reason: flReason } }));
        setTimeout(() => { window.location.href = '/login'; }, 2000);
        break;
      }

      case 'account_status_updated': {
        const nextStatus = msg.data?.status;
        if (!nextStatus) break;
        window.dispatchEvent(new CustomEvent('auth:user-status', { detail: { status: nextStatus } }));
        if (nextStatus === 'suspended') {
          toast.error('Your account has been suspended for restricted features. Contact support for help.', { duration: 6000 });
        } else if (nextStatus === 'active') {
          toast.success('Your account restrictions have been lifted.', { duration: 4000 });
        }
        break;
      }

      case 'maintenance_mode_change': {
        // ONLY redirect normal users — admins always immune
        const adminRoles = ['admin', 'super_admin', 'support_admin', 'team_admin'];
        const isAdminUser = adminRoles.includes(userRef.current?.role);
        if (msg.data?.enabled) {
          if (isAdminUser) {
            toast('🔧 Maintenance mode ON — users redirected', { icon: '⚙️', duration: 4000 });
          } else {
            toast.error('Platform going under maintenance...', { duration: 3000 });
            // Delay redirect so backend cache is cleared before maintenance page mounts
            // (backend clears system_settings_public cache synchronously before WS broadcast)
            setTimeout(() => { window.location.replace('/maintenance'); }, 1500);
          }
        } else {
          if (isAdminUser) {
            toast.success('✅ Maintenance mode OFF — platform live!', { duration: 3000 });
          } else {
            // Platform back online → redirect user from maintenance page
            toast.success('✅ Platform is back online!', { duration: 2000 });
            // Redirect immediately regardless of exact path
            // (user could be on /maintenance or sub-path)
            setTimeout(() => {
              const path = window.location.pathname;
              if (path === '/maintenance' || path.startsWith('/maintenance')) {
                window.location.replace('/dashboard');
              }
            }, 500);
          }
        }
        // Emit so MaintenancePage can also react
        emit('maintenance_mode_change', msg.data);
        break;
      }

      case 'new_announcement':
        emit('new_announcement', msg.data);
        toast(msg.data.title, { icon: '📢', duration: 6000 });
        break;

      case 'permissions_updated':
        // Super admin changed permissions — clear frontend cache so new perms load immediately
        clearPermCache();
        emit('permissions_updated', msg.data);
        break;

      // Support/chat events — most don't have .data wrapper, pass full msg
      case 'ticket_reply':
        emit('ticket_reply', msg);  // { ticket_id, message }
        break;
      case 'ticket_created':
        emit('ticket_created', msg.data || msg);
        break;
      case 'ticket_status_update':
        emit('ticket_status_update', msg.data || msg);
        break;
      case 'ticket_queued':
        emit('ticket_queued', msg);  // { ticket_id }
        break;
      case 'ticket_approved':
        emit('ticket_approved', msg);  // { ticket_id }
        break;
      case 'new_support_ticket':
        emit('new_support_ticket', msg.data || msg);
        break;
      case 'admin_typing':
      case 'admin_stopped_typing':
      case 'user_typing':
      case 'user_stopped_typing':
      case 'admin_online_status':
      case 'user_online_status':
      case 'user_online':
      case 'user_offline':
        emit(msg.type, msg);  // pass full msg (no .data wrapper)
        break;
      case 'attachment_deleted':
      case 'message_deleted':
        emit(msg.type, msg.data || msg);
        break;
      default:
        emit(msg.type, msg.data ?? msg);
    }
  }, [updateBalance, emit]);

  const connectRef = useRef(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const connect = useCallback(() => {
    const token = getToken();
    if (!token || !user) return;

    // Build WS URL safely — same logic as api.js getBaseURL()
    const wsUrl = (() => {
      if (process.env.REACT_APP_WS_URL) return `${process.env.REACT_APP_WS_URL}/ws`;
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const hostname = window.location.hostname;
      const isLocalDev = hostname === 'localhost' || hostname === '127.0.0.1' ||
        /^192\.168\.|^10\.|^172\.(1[6-9]|2[0-9]|3[01])\./.test(hostname);
      if (isLocalDev) return `${proto}//${hostname}:5000/ws`;
      // Production: derive from REACT_APP_API_URL
      if (process.env.REACT_APP_API_URL) {
        const base = process.env.REACT_APP_API_URL.replace(/\/api$/, '').replace(/^https?:/, proto);
        return `${base}/ws`;
      }
      console.warn('[WS] REACT_APP_WS_URL not set — using same-origin WS.');
      return `${proto}//${window.location.host}/ws`;
    })();

    try {
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        retryCountRef.current = 0; // reset on successful connect
        console.log('WebSocket connected');
        try {
          wsRef.current?.send(JSON.stringify({ type: 'auth', token }));
        } catch (err) {
          console.error('WS auth send error:', err);
        }
      };

      wsRef.current.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          handleMessage(msg);
        } catch {}
      };

      wsRef.current.onclose = () => {
        setConnected(false);
        retryCountRef.current += 1;
        if (retryCountRef.current > MAX_RETRIES) {
          console.warn(`[WS] Max retries (${MAX_RETRIES}) reached — stopping reconnect`);
          return;
        }
        // Clear any pending reconnect before scheduling new one
        clearTimeout(reconnectRef.current);
        // Exponential backoff: 3s, 6s, 12s, 24s, 48s
        const delay = Math.min(3000 * Math.pow(2, retryCountRef.current - 1), 48000);
        console.log(`[WS] Reconnecting in ${delay/1000}s (attempt ${retryCountRef.current}/${MAX_RETRIES})`);
        reconnectRef.current = setTimeout(() => {
          if (user) connect();
        }, delay);
      };

      wsRef.current.onerror = () => {
        wsRef.current?.close();
      };
    } catch (err) {
      console.error('WS connect error:', err);
    }
  }, [user, handleMessage]);

  const sendWs = useCallback((data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, []);

  // Keep connectRef up to date without triggering re-runs
  useEffect(() => { connectRef.current = connect; }, [connect]);

  useEffect(() => {
    if (user) {
      // Reset retry count on new user login
      retryCountRef.current = 0;
      connectRef.current?.();
    } else {
      wsRef.current?.close();
      setConnected(false);
      clearTimeout(reconnectRef.current);
    }

    return () => {
      wsRef.current?.close();
      clearTimeout(reconnectRef.current);
    };
  }, [user]); // Only re-run when user changes, not when connect changes

  // Ping to keep alive
  useEffect(() => {
    if (!connected) return;
    const interval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 25000);
    return () => clearInterval(interval);
  }, [connected]);

  return (
    <WebSocketContext.Provider value={{ connected, notifications, subscribe, emit, sendWs,
      reconnect: useCallback(() => {
        // Manual reconnect: reset counter + close existing + reconnect
        retryCountRef.current = 0;
        clearTimeout(reconnectRef.current);
        if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
          wsRef.current.close();
        }
        setTimeout(() => connect(), 100);
      }, [connect])
    }}>
      {children}
    </WebSocketContext.Provider>
  );
}

export const useWebSocket = () => {
  const ctx = useContext(WebSocketContext);
  if (!ctx) throw new Error('useWebSocket must be used within WebSocketProvider');
  return ctx;
};
